import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { EmptyState, fmtMoney } from "@/components/Shared";
import {
  Plus,
  Lightning,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  CloudArrowDown,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import RowMenu from "@/components/RowMenu";
import { toast } from "sonner";

const REPS = [
  "Reliant",
  "TXU Energy",
  "Direct Energy",
  "Constellation",
  "Gexa Energy",
  "Green Mountain",
  "Otro",
];

const blank = {
  name: "",
  kwh_rate: 0.095,
  monthly_avg: 150,
  term_months: 12,
  rep_name: "Reliant",
  type: "fixed",
  active: true,
  notes: "",
};

export default function PlansPage({
  apiBase = "/support/plans",
  readOnly = false,
  section = "Support",
  page = "Planes y tarifas",
} = {}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(apiBase);
      setPlans(data);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [apiBase]);

  const visible = useMemo(() => {
    let out = [...plans];
    if (typeFilter !== "all") out = out.filter((p) => p.type === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.rep_name || "").toLowerCase().includes(q)
      );
    }
    out.sort((a, b) => Number(a.monthly_avg || 0) - Number(b.monthly_avg || 0));
    return out;
  }, [plans, search, typeFilter]);

  const stats = useMemo(() => {
    const active = plans.filter((p) => p.active).length;
    const rates = plans.map((p) => Number(p.kwh_rate || 0)).filter((n) => n > 0);
    const avgRate = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
    const fixed = plans.filter((p) => p.type === "fixed").length;
    const variable = plans.filter((p) => p.type === "variable").length;
    return { active, avgRate, fixed, variable, total: plans.length };
  }, [plans]);

  const startCreate = () => {
    setEditId(null);
    setForm(blank);
    setOpen(true);
  };
  const startEdit = (p) => {
    setEditId(p.id);
    setForm({ ...blank, ...p });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        kwh_rate: Number(form.kwh_rate),
        monthly_avg: Number(form.monthly_avg),
        term_months: Number(form.term_months),
      };
      if (editId) await api.put(`${apiBase}/${editId}`, payload);
      else await api.post(apiBase, payload);
      toast.success(editId ? "Plan actualizado" : "Plan creado");
      setOpen(false);
      setForm(blank);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este plan?")) return;
    try {
      await api.delete(`${apiBase}/${id}`);
      toast.success("Plan eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const syncFromSquare = async () => {
    if (
      !confirm(
        "¿Importar planes desde Square Catalog?\n\nLos planes existentes (matched por Square ID) se actualizarán con el nombre y precio actuales. Los campos kWh, término y REP se mantienen."
      )
    )
      return;
    try {
      const { data } = await api.post("/square/sync-plans");
      const parts = [];
      if (data.created) parts.push(`${data.created} creado(s)`);
      if (data.updated) parts.push(`${data.updated} actualizado(s)`);
      if (data.skipped) parts.push(`${data.skipped} omitido(s)`);
      toast.success(
        `Square: ${parts.join(" · ") || "sin cambios"} de ${data.total_in_square} en catálogo`
      );
      if (data.errors?.length)
        toast.error(`${data.errors.length} errores — primero: ${data.errors[0]}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <PageShell
      section={section}
      page={page}
      context={`${plans.length} planes · ${stats.active} activos`}
      testid="plans-toolbar"
      actions={
        readOnly ? null : (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={syncFromSquare}
              data-testid="plans-square-sync"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white rounded transition-colors h-9"
              title="Importar planes desde Square Catalog"
            >
              <CloudArrowDown size={14} /> Square
            </button>
            <Button
              onClick={startCreate}
              className="bg-slate-900 hover:bg-slate-800 text-white h-9"
              data-testid="add-plan"
            >
              <Plus size={15} className="mr-1.5" /> Nuevo plan
            </Button>
          </div>
        )
      }
    >
      {/* ── Corporate summary strip ── */}
      <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 dark:divide-slate-800">
          <SummaryCell
            accent="bg-emerald-500"
            label="Planes activos"
            value={stats.active}
            hint={`de ${stats.total} totales`}
            mono
          />
          <SummaryCell
            accent="bg-slate-900 dark:bg-slate-100"
            label="Tarifa kWh media"
            value={stats.avgRate ? `${(stats.avgRate).toFixed(4)}¢` : "—"}
            hint="promedio ponderado"
            mono
          />
          <SummaryCell
            accent="bg-blue-500"
            label="Planes fijos"
            value={stats.fixed}
            hint="tarifa estable"
            mono
          />
          <SummaryCell
            accent="bg-amber-500"
            label="Planes variables"
            value={stats.variable}
            hint="tarifa dinámica"
            mono
          />
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            placeholder="Buscar por nombre o REP…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
            data-testid="plans-search"
          />
        </div>
        <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-800 rounded-md p-0.5 bg-white dark:bg-slate-950">
          {["all", "fixed", "variable"].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              data-testid={`plans-filter-${t}`}
              className={`px-3 h-8 text-[11px] font-medium uppercase tracking-wider rounded-sm transition-colors ${
                typeFilter === t
                  ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              {t === "all" ? "Todos" : t === "fixed" ? "Fijos" : "Variables"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Corporate card grid ── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md p-5"
            >
              <div className="h-3 w-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse mb-3" />
              <div className="h-5 w-32 bg-slate-100 dark:bg-slate-800 rounded animate-pulse mb-6" />
              <div className="grid grid-cols-2 gap-3">
                <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Lightning}
          title={plans.length === 0 ? "Sin planes" : "Sin resultados"}
          message={
            plans.length === 0
              ? "Crea tu primer plan de energía. Define kWh rate, ticket promedio mensual, término y REP."
              : "Ajusta la búsqueda o los filtros."
          }
          action={
            readOnly || plans.length !== 0 ? null : (
              <Button
                onClick={startCreate}
                className="bg-slate-900 hover:bg-slate-800 text-white h-9"
              >
                <Plus size={15} className="mr-1.5" /> Nuevo plan
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className="group relative bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md hover:border-slate-400 dark:hover:border-slate-600 transition-colors overflow-hidden"
            >
              {/* Top accent strip — sober monochrome */}
              <div
                className={`h-[2px] w-full ${
                  p.active
                    ? "bg-slate-900 dark:bg-slate-100"
                    : "bg-slate-200 dark:bg-slate-800"
                }`}
              />

              {/* Header */}
              <div className="px-4 pt-4 pb-3 border-b border-slate-100 dark:border-slate-900">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                      {p.rep_name || "—"}
                    </div>
                    <div className="text-base font-semibold text-slate-900 dark:text-slate-100 mt-1 truncate">
                      {p.name}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                      p.type === "fixed"
                        ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300"
                        : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300"
                    }`}
                  >
                    {p.type === "fixed" ? "Fijo" : "Variable"}
                  </span>
                </div>
              </div>

              {/* Metrics grid */}
              <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
                    Tarifa kWh
                  </div>
                  <div className="mt-0.5 font-mono tabular-nums text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight">
                    {Number(p.kwh_rate).toFixed(4)}
                    <span className="text-[10px] text-slate-400 ml-0.5">¢</span>
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
                    Promedio /mes
                  </div>
                  <div className="mt-0.5 font-mono tabular-nums text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight">
                    {fmtMoney(p.monthly_avg)}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
                    Término
                  </div>
                  <div className="mt-0.5 font-mono tabular-nums text-sm font-semibold text-slate-700 dark:text-slate-300 leading-tight">
                    {p.term_months}
                    <span className="text-[10px] text-slate-400 ml-0.5">meses</span>
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
                    Estado
                  </div>
                  <div className="mt-0.5 text-sm font-semibold leading-tight">
                    {p.active ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                        <CheckCircle size={12} weight="fill" /> Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500">
                        <XCircle size={12} weight="fill" /> Inactivo
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes (optional) */}
              {p.notes && (
                <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-900 bg-slate-50/50 dark:bg-slate-900/40">
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-snug">
                    {p.notes}
                  </div>
                </div>
              )}

              {/* Footer actions */}
              {!readOnly && (
                <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-900 flex items-center justify-end gap-0.5">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`plan-edit-${p.id}`}
                    className="inline-flex items-center gap-1 px-2 h-7 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                    title="Editar plan"
                  >
                    <PencilSimple size={12} /> Editar
                  </button>
                  <RowMenu
                    testid={`plan-menu-${p.id}`}
                    items={[
                      {
                        label: "Editar",
                        icon: PencilSimple,
                        onClick: () => startEdit(p),
                        testid: `plan-menu-edit-${p.id}`,
                      },
                      { type: "separator" },
                      {
                        label: "Eliminar",
                        icon: Trash,
                        onClick: () => remove(p.id),
                        testid: `plan-del-${p.id}`,
                        danger: true,
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Editor dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-xl p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"
          data-testid="plan-form-dialog"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                {editId ? "Editar" : "Nuevo"} · Plan
              </div>
              {editId && form.name && (
                <>
                  <div className="h-3 w-px bg-slate-300 dark:bg-slate-700" />
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[280px]">
                    {form.name}
                  </div>
                </>
              )}
            </div>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                form.active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-400"
                  : "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  form.active ? "bg-emerald-500" : "bg-slate-400"
                }`}
              />
              {form.active ? "Activo" : "Inactivo"}
            </span>
          </div>

          <div className="px-5 py-4">
            <div className="grid grid-cols-12 gap-x-4 gap-y-3">
              <FormField label="Nombre del plan *" span={12}>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="plan-name"
                  placeholder="ej: FlexSave 12"
                  className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </FormField>

              <FormField label="Proveedor (REP) *" span={7}>
                <select
                  value={form.rep_name}
                  onChange={(e) => setForm({ ...form, rep_name: e.target.value })}
                  data-testid="plan-rep"
                  className="w-full h-9 text-sm px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
                >
                  {REPS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Tipo *" span={5}>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full h-9 text-sm px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
                >
                  <option value="fixed">Fijo</option>
                  <option value="variable">Variable</option>
                </select>
              </FormField>

              <div className="col-span-12 flex items-center gap-2 pt-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500 font-semibold">
                  Tarifas
                </div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
              </div>

              <FormField label="Tarifa kWh (¢) *" span={4}>
                <Input
                  type="number"
                  step="0.0001"
                  value={form.kwh_rate}
                  onChange={(e) => setForm({ ...form, kwh_rate: e.target.value })}
                  className="h-9 text-sm font-mono tabular-nums border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </FormField>
              <FormField label="Promedio mensual ($)" span={4}>
                <Input
                  type="number"
                  step="0.01"
                  value={form.monthly_avg}
                  onChange={(e) => setForm({ ...form, monthly_avg: e.target.value })}
                  className="h-9 text-sm font-mono tabular-nums border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </FormField>
              <FormField label="Término (meses)" span={4}>
                <Input
                  type="number"
                  value={form.term_months}
                  onChange={(e) => setForm({ ...form, term_months: e.target.value })}
                  className="h-9 text-sm font-mono tabular-nums border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </FormField>

              <div className="col-span-12 flex items-center justify-between border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 bg-slate-50/50 dark:bg-slate-900/50">
                <div className="flex flex-col">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Plan activo
                  </Label>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Los planes inactivos no aparecen en las opciones de nuevos clientes
                  </span>
                </div>
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="plan-active"
                />
              </div>

              <FormField label="Notas internas" span={12}>
                <Textarea
                  rows={2}
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none"
                />
              </FormField>
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              {editId ? "Los cambios se aplican al guardar" : "El plan estará disponible al crearlo"}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="h-8 text-xs border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={!form.name}
                data-testid="save-plan"
                className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                {editId ? "Guardar cambios" : "Crear plan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Small local helpers (kept in-file to avoid new files)
// ─────────────────────────────────────────────────────────────

function SummaryCell({ accent, label, value, hint, mono = false }) {
  return (
    <div className="px-5 py-4 relative">
      <div className={`absolute top-0 left-0 right-0 h-[2px] ${accent}`} />
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
        {label}
      </div>
      <div
        className={`mt-1.5 text-2xl font-semibold text-slate-900 dark:text-slate-100 ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function FormField({ label, children, span = 12 }) {
  const spanCls = {
    4: "col-span-12 sm:col-span-4",
    5: "col-span-12 sm:col-span-5",
    6: "col-span-12 sm:col-span-6",
    7: "col-span-12 sm:col-span-7",
    8: "col-span-12 sm:col-span-8",
    12: "col-span-12",
  }[span];
  return (
    <div className={`${spanCls} space-y-1`}>
      <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </Label>
      {children}
    </div>
  );
}
